import React, { useMemo, useState } from "react";
import ModalContainer from "../common/ModalContainer";
import moment from "moment";
import { formatMoney } from "../../features/utils/formatMoney";
import { useInsuranceApi } from "../../hooks/useInsuranceApi";
import { useDispatch, useSelector } from "react-redux";
import { setInsuranceOrders } from "../../redux/reducers/orderSlice";
import { templateInsuranceOrderData } from "../../data/order/ordersData";

function getTravellers(booking) {
  return (
    booking?.orderData?.orderData?.requestBody?.personsInfo ||
    booking?.orderData?.requestBody?.personsInfo ||
    []
  );
}

function getPolicyType(booking) {
  return (
    booking?.title ||
    booking?.orderData?.orderData?.requestBody?.policyType ||
    booking?.orderData?.orderData?.policyType ||
    "Insurance"
  );
}

function getAmount(booking) {
  return (
    booking?.grandTotal ||
    booking?.fareTotal ||
    booking?.amount ||
    booking?.orderData?.orderData?.totalAmount ||
    booking?.orderData?.orderData?.amount ||
    0
  );
}

function getCurrency(booking) {
  return (
    booking?.originalCurrency ||
    booking?.currency ||
    booking?.orderData?.orderData?.currency ||
    booking?.orderData?.orderData?.originalCurrency ||
    "NGN"
  );
}

export const CancelInsuranceBookingModal = ({
  setOpen,
  open,
  booking,
  noSave,
}) => {
  const dispatch = useDispatch();
  const { insuranceOrders } = useSelector((state) => state.order);
  const { cancelAndRefundInsurance, declineAwaitingAdminInsuranceOrder } =
    useInsuranceApi();
  const [loading, setLoading] = useState(false);

  const isAwaitingAdmin = ["AwaitingAdmin", "Awaiting Admin"].includes(
    booking?.status,
  );

  const travellers = useMemo(() => getTravellers(booking), [booking]);
  const policyType = getPolicyType(booking);
  const amount = getAmount(booking);
  const currency = getCurrency(booking);
  const provider = booking?.supplier || booking?.provider || "—";
  const bookingRef =
    booking?.bookingId || booking?.mainBooking?.bookingId || booking?.bookRef;
  const destination =
    booking?.orderData?.orderData?.requestBody?.destinationCountry || "—";

  const travellerSummary = travellers.length
    ? travellers
        .map((person) =>
          [person?.firstName, person?.lastName].filter(Boolean).join(" "),
        )
        .filter(Boolean)
        .join(", ")
    : booking?.name || "—";

  const coverPeriod =
    booking?.startDate && booking?.endDate
      ? `${moment(booking.startDate).format("DD MMM YYYY")} – ${moment(
          booking.endDate,
        ).format("DD MMM YYYY")}`
      : "—";

  const refundAmount =
    booking?.cancellationAmount ??
    (booking?.grandTotal != null && booking?.cancellationFee != null
      ? Number(booking.grandTotal) - Number(booking.cancellationFee)
      : null);

  const updateOrdersList = (updatedBooking) => {
    if (!noSave) return;
    const mergedBooking = {
      ...booking,
      ...updatedBooking,
      account:
        typeof updatedBooking?.account === "object"
          ? updatedBooking.account
          : booking?.account,
      mainBooking: updatedBooking?.mainBooking || booking?.mainBooking,
    };
    dispatch(
      setInsuranceOrders(
        insuranceOrders?.map((order) =>
          order?._id === booking?._id
            ? templateInsuranceOrderData(mergedBooking)
            : order,
        ),
      ),
    );
  };

  const handleCancel = async () => {
    if (isAwaitingAdmin) {
      await declineAwaitingAdminInsuranceOrder({
        booking,
        setLoading,
        callbackUrl: (declinedBooking) => {
          updateOrdersList(declinedBooking);
          setOpen(false);
        },
      });
      return;
    }

    await cancelAndRefundInsurance({
      booking,
      setLoading,
      callback: (cancelledData) => {
        // console.log({ cancelledData });
        updateOrdersList(cancelledData);
        setOpen(false);
      },
      noSave,
    });
  };

  return (
    <ModalContainer
      open={open}
      setOpen={setOpen}
      secondary
      padding="p-4 sm:p-6"
      childClassname="max-w-[560px]"
      title={isAwaitingAdmin ? "Decline insurance booking" : "Cancel insurance"}
      subTitle={
        isAwaitingAdmin
          ? "This booking is awaiting admin approval. Declining will cancel it."
          : "Review the booking details before confirming cancellation."
      }
    >
      <div className="w-full flex flex-col gap-5">
        <div className="rounded-xl border border-[#E8EEF5] bg-[#F8FAFC] p-4 flex flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="h-10 w-10 rounded-lg bg-white border border-[#E0E0E0] flex items-center justify-center shrink-0">
                <img
                  src="/IconInsurance2.svg"
                  alt=""
                  className="h-5 object-contain"
                />
              </div>
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-base font-semibold text-[#1A1A1A]">
                  {policyType}
                </span>
                <span className="text-sm text-[#6D7C94]">{provider}</span>
              </div>
            </div>
            <span className="shrink-0 text-xs font-medium px-2.5 py-1 rounded-full bg-white border border-[#E0E0E0] text-[#6D7C94]">
              {booking?.status || "—"}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <DetailItem label="Booking ref" value={bookingRef || "—"} />
            <DetailItem label="Destination" value={destination} />
            <DetailItem label="Cover period" value={coverPeriod} />
            <DetailItem
              label="Amount"
              value={formatMoney(
                amount,
                currency === "NGN" ? undefined : currency,
              )}
            />
          </div>

          <div className="pt-3 border-t border-[#E8EEF5]">
            <DetailItem
              label={`Traveller${travellers.length === 1 ? "" : "s"} (${travellers.length || 0})`}
              value={travellerSummary}
            />
          </div>
        </div>

        {isAwaitingAdmin ? (
          <div className="rounded-lg border border-[#F5E6B8] bg-[#FDF8E8] px-4 py-3 text-sm text-[#6B5618]">
            Declining this booking will reject the agent’s request. No ticket
            will be issued.
          </div>
        ) : (
          <div className="rounded-xl border border-[#E8EEF5] bg-white p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-[#6D7C94]">Estimated refund</span>
              <span className="font-semibold text-[#1A1A1A]">
                {refundAmount != null
                  ? formatMoney(
                      refundAmount,
                      currency === "NGN" ? undefined : currency,
                    )
                  : "—"}
              </span>
            </div>
            <p className="text-xs text-[#6D7C94] m-0">
              By confirming, you accept any applicable cancellation charges.
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={() => setOpen(false)}
            disabled={loading}
            className="flex-1 h-12 rounded-lg border border-[#DBDBDB] bg-white text-sm font-semibold text-[#6D7C94] hover:bg-[#F8FAFC] disabled:opacity-60"
          >
            Keep booking
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="flex-1 h-12 rounded-lg bg-[#B81F25] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 flex items-center justify-center"
          >
            {loading ? (
              <div className="buttonLoader" />
            ) : isAwaitingAdmin ? (
              "Decline booking"
            ) : (
              "Cancel booking"
            )}
          </button>
        </div>
      </div>
    </ModalContainer>
  );
};

function DetailItem({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-xs text-[#6D7C94]">{label}</span>
      <span className="text-sm font-medium text-[#1A1A1A] break-words">
        {value}
      </span>
    </div>
  );
}
